import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Truck } from 'lucide-react';

import {
  useOrderDeliveryTracking,
  useDeliveryCompanies,
  useUpdateDeliveryTracking,
  useUpdateDeliveryStatus,
  type DeliveryStatus,
} from '@/features/delivery';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/molecules/select';
import { formatDate } from '@/lib/utils';

interface DeliveryTrackingManagerProps {
  orderId: string;
}

// Granular carrier status flow (server-side, distinct from the coarse
// order-level status handled by the "Update Status" card).
const STATUS_FLOW: DeliveryStatus[] = [
  'pending',
  'confirmed',
  'picked_up',
  'in_transit',
  'out_for_delivery',
  'delivered',
];

const TERMINAL_STATUSES: DeliveryStatus[] = ['delivered', 'failed'];

function getNextStatus(current: DeliveryStatus): DeliveryStatus | null {
  const index = STATUS_FLOW.indexOf(current);
  if (index === -1 || index === STATUS_FLOW.length - 1) return null;
  return STATUS_FLOW[index + 1];
}

export function DeliveryTrackingManager({ orderId }: DeliveryTrackingManagerProps) {
  const { t } = useTranslation();
  const { data: trackings, isLoading } = useOrderDeliveryTracking(orderId);
  const { data: companies = [] } = useDeliveryCompanies();
  const { mutate: saveTracking, isPending: isSaving } = useUpdateDeliveryTracking();
  const { mutate: advanceStatus, isPending: isAdvancing } = useUpdateDeliveryStatus();

  // A given order may have several tracking records over its lifetime
  // (e.g. multi-parcel); take the first / active one.
  const tracking = trackings?.[0];

  const [carrierId, setCarrierId] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  // Pre-fill the form whenever the underlying tracking record changes
  // (initial load, or after a save invalidates the query).
  useEffect(() => {
    setCarrierId(tracking?.deliveryCompany?.refId ?? '');
    setTrackingNumber(tracking?.trackingNumber ?? '');
  }, [tracking?.refId, tracking?.deliveryCompany?.refId, tracking?.trackingNumber]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="size-5" />
            {t('orders.deliveryTracking.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!tracking) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="size-5" />
            {t('orders.deliveryTracking.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-2">
            {t('orders.deliveryTracking.noTracking')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const isTerminal = TERMINAL_STATUSES.includes(tracking.status);
  const nextStatus = getNextStatus(tracking.status);

  const handleSaveTracking = () => {
    saveTracking({
      refId: tracking.refId,
      orderId,
      deliveryCompanyRefId: carrierId || undefined,
      trackingNumber: trackingNumber || undefined,
    });
  };

  const handleAdvanceStatus = () => {
    if (!nextStatus) return;
    advanceStatus({ refId: tracking.refId, orderId, status: nextStatus });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="size-5" />
          {t('orders.deliveryTracking.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Carrier + tracking number */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`delivery-carrier-${tracking.refId}`}>
              {t('orders.deliveryTracking.carrierLabel')}
            </Label>
            <Select value={carrierId} onValueChange={setCarrierId}>
              <SelectTrigger id={`delivery-carrier-${tracking.refId}`} className="w-full">
                <SelectValue placeholder={t('orders.deliveryTracking.carrierPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.refId} value={company.refId}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`delivery-tracking-number-${tracking.refId}`}>
              {t('orders.deliveryTracking.trackingNumberLabel')}
            </Label>
            <Input
              id={`delivery-tracking-number-${tracking.refId}`}
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder={t('orders.deliveryTracking.trackingNumberPlaceholder')}
            />
          </div>

          <Button
            onClick={handleSaveTracking}
            disabled={isSaving}
            loading={isSaving}
            className="w-full"
          >
            {isSaving ? t('orders.deliveryTracking.saving') : t('orders.deliveryTracking.saveButton')}
          </Button>
        </div>

        {/* Granular status advance */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center justify-between">
            <Label>{t('orders.deliveryTracking.statusSectionTitle')}</Label>
            <Badge variant="outline">
              {t(`orders.deliveryTracking.statuses.${tracking.status}`)}
            </Badge>
          </div>

          {isTerminal ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              {t('orders.deliveryTracking.terminalNote')}
            </p>
          ) : nextStatus ? (
            <Button
              variant="outline"
              onClick={handleAdvanceStatus}
              disabled={isAdvancing}
              loading={isAdvancing}
              className="w-full"
            >
              {isAdvancing
                ? t('orders.deliveryTracking.updating')
                : t('orders.deliveryTracking.markAs', {
                    status: t(`orders.deliveryTracking.statuses.${nextStatus}`),
                  })}
            </Button>
          ) : null}
        </div>

        {/* Tracking history */}
        {tracking.trackingHistory && tracking.trackingHistory.length > 0 && (
          <div className="space-y-2 pt-4 border-t">
            <p className="text-sm font-medium text-muted-foreground">
              {t('orders.deliveryTracking.historyTitle')}
            </p>
            <ul className="space-y-2">
              {tracking.trackingHistory.map((event, index) => (
                <li
                  key={`${event.timestamp}-${index}`}
                  className="flex items-start justify-between gap-2 text-sm"
                >
                  <div>
                    <p className="font-medium">
                      {t(`orders.deliveryTracking.statuses.${event.status}`, {
                        defaultValue: event.status,
                      })}
                    </p>
                    {event.location && (
                      <p className="text-muted-foreground text-xs">{event.location}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatDate(event.timestamp, 'dd/MM HH:mm')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
